import { useEffect, useMemo, useState } from 'react';
import imgLogowithlcon1 from '../../assets/logo-cherry-color.webp';
import {
  getBookingDraft,
  patchBookingDraft,
  useBookingDraft,
} from '../../lib/booking-draft';
import {
  previewBookingPricing,
  previewBookingQuote,
  previewVoucherCode,
  type BookingPricePreview,
  type BookingQuote,
  type BookingQuoteItem,
  type ServiceAddonRow,
  type VoucherPreview,
} from '../../lib/booking-api';
import { getMyMembership } from '../../lib/customer-api';
import { isCustomerAuthenticated } from '../../lib/customer-auth';
import { apiErrorMessage } from '../../lib/api';

interface BookingBasketProps {
  price: number;
  duration?: string;
  hasPets?: string;
  /** Whether this category's flow even asks the "Do you have pets?"
   *  question (cleaning does; childcare doesn't). When false the basket
   *  hides the pets line — otherwise non-cleaning flows showed a stray
   *  "No pets in property" just because sessionStep passed 4. */
  showPets?: boolean;
  /** Real Date object from the wizard's calendar pick. Replaces the
   *  hard-coded "Sat 7 Mar" string the basket used to show — we now
   *  format from a real date so the basket actually reflects the
   *  customer's choice. */
  appointmentDate?: Date | string | null;
  appointmentTime?: string;
  selectedTimes?: string[];
  /** The "Other times you've kept open" list is a cleaning-only concept
   *  (the customer leaves several slots open for the pro to confirm). It
   *  confused customers in hair, beauty & wellness, and childcare, so the
   *  basket only renders that block when the active category is Cleaning. */
  isCleaning?: boolean;
  onSubscribeClick: () => void;
  /** Gate for the "Want to save on this booking?" membership upsell. The
   *  £15/mo plan covers standard cleaning + hair/beauty/childcare, but NOT
   *  the premium one-off cleans (deep / end-of-tenancy / commercial). The
   *  caller passes false for those so the prompt never shows — a frontend
   *  guard that doesn't depend on the server quote's membership_eligible
   *  flag having loaded (or being deployed). Defaults to true. */
  allowMembershipUpsell?: boolean;
  sessionStep?: number;
  selectedAddons?: { id: string; duration: string }[];
  /** Live add-on catalog from `/public/service-addons`. Used to
   *  resolve the `selectedAddons[].id` UUID into the admin-managed
   *  label — replaces the hard-coded ADDON_LABELS dictionary. */
  addonsCatalog?: ServiceAddonRow[];
  serviceName?: string;
  /** Category label for the section heading. Falls back to "Service"
   *  so the sidebar still reads sensibly while the catalog is loading. */
  categoryName?: string;
  commercialSpaceType?: string;
  /** Commercial-space options for the active category, sourced from
   *  `category.flow_config.commercial_spaces`. Lets the basket resolve
   *  `commercialSpaceType` (the picked id) back to a human label. */
  commercialSpaceOptions?: Array<{ id: string; label: string }>;
  otherCommercialSpace?: string;
  /** Hours for a COMMERCIAL booking, derived from the workspace-size
   *  selection (recommended_hours, customer-overridable). Drives the
   *  per-hour line total + the "For Xh" line so the basket tracks the
   *  chosen hours instead of billing a flat 1h. Undefined until a size
   *  is picked. */
  commercialHours?: number;
  /** How the service is priced: PER_HOUR (multiply by duration) or
   *  PER_BOOKING (flat price). Without this the basket was always
   *  multiplying by hours, so a £80 PER_BOOKING standard clean showed
   *  as £80 × 3 = £240. */
  pricingUnit?: 'PER_HOUR' | 'PER_BOOKING';
  /** Wallet balance the customer chose to apply to this booking. We
   *  cap it at the post-discount subtotal so the displayed total
   *  matches what the backend will charge (server caps + final). */
  walletAmount?: number;
  walletCurrency?: string;
  /** Total wallet balance the customer has available. When > 0 the
   *  basket renders a "Use my wallet balance" toggle below the
   *  coupon code so the customer can opt in / out of applying it. */
  walletAvailable?: number;
  /** Whether the wallet toggle is currently on. Lifted to the parent
   *  so /your-appointment can also feed `wallet_amount` into the
   *  Stripe checkout request. */
  walletEnabled?: boolean;
  onToggleWallet?: (enabled: boolean) => void;
  /** Optional — parent may pass this to let the customer type the exact
   *  amount they want pulled from their wallet (rather than the basket
   *  always burning the full balance). The basket clamps the value to
   *  [0, min(walletAvailable, afterDiscount)] before propagating. */
  onWalletAmountChange?: (amount: number) => void;
  /** Set on the recurring-booking basket. Stripe Subscriptions take
   *  the full per-cycle amount each invoice — vouchers, wallets, and
   *  membership discounts don't apply (matches the backend's
   *  `createSubscription`), so the basket suppresses those lines for
   *  subscriptions to keep the displayed total honest. */
  isSubscription?: boolean;
  /** Single source of truth for the basket. When provided, the basket
   *  POSTs these to `/public/booking/quote` and renders the response
   *  verbatim — same recipe createOneOff / createSubscription will
   *  use at checkout, so the displayed total = the Stripe charge. */
  quoteItems?: BookingQuoteItem[];
  /** Fires whenever the server quote refreshes — the parent page uses
   *  this to capture `pricing_version`, which it then echoes back on
   *  createOneOff/createSubscription. Without it the parent has no
   *  way to know the basket's pricing token so the rate-lock can't
   *  fire. The full BookingQuote is passed so future parents can also
   *  reach in for the latest totals without re-fetching themselves. */
  onQuoteChange?: (quote: BookingQuote | null) => void;
}

export function BookingBasket({
  price,
  duration = '3h',
  hasPets = 'yes',
  showPets = true,
  appointmentDate = null,
  appointmentTime = '',
  // selectedTimes / isCleaning props are retained on the type for caller
  // compatibility but no longer rendered — the "Other times you've kept
  // open" secondary-slot list was removed (customers can't select multiple
  // times, so it only duplicated the chosen slot).
  onSubscribeClick,
  allowMembershipUpsell = true,
  sessionStep = 1,
  selectedAddons = [],
  addonsCatalog = [],
  serviceName = '',
  categoryName,
  commercialSpaceType,
  commercialSpaceOptions = [],
  commercialHours,
  otherCommercialSpace,
  pricingUnit = 'PER_HOUR',
  walletAmount = 0,
  walletCurrency = 'GBP',
  walletAvailable = 0,
  walletEnabled = false,
  onToggleWallet,
  onWalletAmountChange,
  isSubscription = false,
  quoteItems,
  onQuoteChange,
}: BookingBasketProps) {
  // O(1) lookup for the label resolvers below.
  const addonNameById = useMemo(
    () => new Map(addonsCatalog.map((a) => [a.id, a.name])),
    [addonsCatalog],
  );
  const commercialSpaceLabelById = useMemo(
    () => new Map(commercialSpaceOptions.map((s) => [s.id, s.label])),
    [commercialSpaceOptions],
  );
  // Live draft so the basket's address/category reflects whatever the
  // customer just picked — the legacy "45 West Street EC59 8FB" line
  // was a leftover from the Figma mock.
  const draft = useBookingDraft();
  const headerAddress =
    draft?.address?.address_line1 ??
    'Add your address on the previous step.';
  const sectionHeading = categoryName ?? 'Service';

  const appointmentDateObj = useMemo<Date | null>(() => {
    if (!appointmentDate) return null;
    if (appointmentDate instanceof Date) return appointmentDate;
    const parsed = new Date(appointmentDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [appointmentDate]);

  // Single appointment date line: the full long date, with the chosen
  // time appended. (Previously a short heading + a long date both
  // rendered, which showed the date twice in the summary.)
  const appointmentHeading = useMemo(() => {
    if (!appointmentDateObj) return '';
    const dateLabel = appointmentDateObj.toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return appointmentTime ? `${dateLabel} at ${appointmentTime}` : dateLabel;
  }, [appointmentDateObj, appointmentTime]);
  // Restore any previously-entered code from the in-flight booking
  // draft so a user who navigated between steps doesn't have to retype
  // it. The actual redemption happens server-side at checkout — this
  // basket only stashes the code into the draft.
  const [couponCode, setCouponCode] = useState<string>(() => {
    const saved = getBookingDraft()?.meta?.coupon_code;
    return typeof saved === 'string' ? saved : '';
  });
  const [voucherApplied, setVoucherApplied] = useState<VoucherPreview | null>(
    null,
  );
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);

  const trimmedCode = couponCode.trim();
  const canApply =
    trimmedCode.length > 0 && !voucherApplied && !couponLoading;

  // Parse the duration id the admin configured for this category.
  // Accepts the historic "3h" / "3h30" form AND newer plain-number
  // ids like "3", "3.5", or "210min". Returns hours as a float.
  // Without the broader regex the basket multiplied price by 0 hours
  // whenever the admin used ids without an 'h' suffix — the total
  // collapsed to £0 (or to add-ons-only once add-ons were picked).
  const parseDuration = (dur: string) => {
    if (!dur) return 0;
    const minsOnly = /^(\d+)\s*min$/i.exec(dur);
    if (minsOnly) return parseInt(minsOnly[1], 10) / 60;
    const hAndM = /^(\d+)h(\d+)?$/i.exec(dur);
    if (hAndM) {
      const h = parseInt(hAndM[1], 10);
      const m = hAndM[2] ? parseInt(hAndM[2], 10) : 0;
      return h + m / 60;
    }
    const num = parseFloat(dur);
    return Number.isFinite(num) ? num : 0;
  };

  // Spell out the unit so the basket reads "For 2 hours" rather than an
  // ambiguous "For 2". Singular for exactly 1h; keeps decimals (2.5 hours).
  const formatHours = (h: number) => `${h} ${h === 1 ? 'hour' : 'hours'}`;

  // Commercial bookings skip the residential duration/pets steps —
  // `selectedDuration` is auto-seeded to "2h" but the customer never
  // confirms it. Instead they pick a workspace size (which carries a
  // recommended hours value) and can override the hours, both of which
  // arrive here as `commercialHours`. We bill the hourly rate × those
  // hours (so the total tracks the chosen hours) and suppress the
  // residential duration/pets lines below.
  const isCommercial = !!commercialSpaceType;
  const durationHours = parseDuration(duration);
  // Residential: duration isn't picked until step 3 (the same point the
  // "For {duration}" line appears below), so before that we bill 1h as
  // the baseline instead of multiplying by the seeded default.
  const billableHours = isCommercial
    ? commercialHours && commercialHours > 0
      ? commercialHours
      : 1
    : sessionStep >= 3
      ? durationHours
      : 1;
  // Flat-price services (PER_BOOKING) bill the headline price once.
  // PER_HOUR services multiply by the chosen hours. Without this gate
  // the basket overcharged £price × hours for every flat-price service.
  const cleaningTotal =
    pricingUnit === 'PER_BOOKING' ? price : price * billableHours;

  // Add-ons total uses each add-on's own catalog price + block size —
  // NOT the service price. The legacy "0.5 × service price for 30min"
  // formula massively over- / under-charged whenever the service was
  // priced very differently from the add-ons (e.g. an £80 PER_BOOKING
  // standard clean + a £14 mattress add-on).
  const addonsTotal = selectedAddons.reduce((acc, addon) => {
    const catalogRow = addonsCatalog.find((a) => a.id === addon.id);
    if (catalogRow) {
      // Inclusive (display) price so this local preview matches the all-in
      // server quote; falls back to the raw price on older API builds.
      const blockPrice = Number(catalogRow.display_price ?? catalogRow.price);
      const oneHourBlocks = Math.max(
        1,
        Math.round(60 / (catalogRow.block_minutes || 30)),
      );
      const blocks = addon.duration === '1hour' ? oneHourBlocks : 1;
      return acc + blockPrice * blocks;
    }
    // Catalog not loaded yet — fall back to a safe zero so the basket
    // doesn't show a number we'd then have to correct on re-render.
    return acc;
  }, 0);

  const subtotal = cleaningTotal + addonsTotal;

  // Pull the customer's active membership benefit so the basket
  // applies the same subscription discount the backend will apply at
  // checkout. Without this the basket showed a number that didn't
  // match what Stripe actually charged (e.g. £73.07 vs £64.67 for a
  // 15%-off customer).
  const [membershipDiscountPct, setMembershipDiscountPct] = useState(0);
  const [hasActiveMembership, setHasActiveMembership] = useState(false);
  useEffect(() => {
    if (!isCustomerAuthenticated()) return;
    let cancelled = false;
    getMyMembership()
      .then((res) => {
        if (cancelled) return;
        // Only treat the customer as a current member when their plan
        // is in a live state — ACTIVE / TRIALING / PENDING. A row in
        // CANCELLED or EXPIRED still comes back on `info` but those
        // customers should still see the upsell because their perks
        // are gone. `benefits` is computed by the backend's
        // getActiveBenefitsForUser which already filters by live
        // status, so its presence is the strictest signal.
        const liveStatuses = new Set(['ACTIVE', 'TRIALING', 'PENDING']);
        const liveByInfo = !!(
          res?.info && liveStatuses.has(String(res.info.status))
        );
        if (liveByInfo || res?.benefits) {
          setHasActiveMembership(true);
        }
        const pct = Number(res?.benefits?.discount_percent ?? 0);
        if (pct > 0) setMembershipDiscountPct(pct);
      })
      .catch(() => {
        /* non-blocking — basket falls back to full price */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Subscription benefit applies to the line-item subtotal (matches
  // the backend's `applyChargesAndTax` neighbour code). Recurring
  // (Stripe Subscription) baskets skip it — `createSubscription`
  // bills the full per-cycle amount, no membership / voucher /
  // wallet stacking at signup time.
  // Defaults computed locally for the legacy callsites; overridden
  // below when the server quote is available.
  // Membership discount applies whether the customer is booking a
  // one-off or a recurring service — if they hold a Cherry Picked
  // Plus plan, every booking benefits. Voucher + wallet remain
  // one-off-only (subscription mode skips them in the basket below).
  const subscriptionDiscountLocal =
    membershipDiscountPct > 0
      ? Math.round(subtotal * (membershipDiscountPct / 100) * 100) / 100
      : 0;
  const voucherDiscountLocal =
    !isSubscription && voucherApplied ? voucherApplied.discount_amount ?? 0 : 0;

  // Two-mode pricing:
  //   1. NEW (`quoteItems` provided) — POST /public/booking/quote
  //      runs the exact backend recipe (priceItems + tax + charges +
  //      membership discount + voucher + wallet) and we render the
  //      response verbatim. No local math, so basket totals always
  //      equal what Stripe will charge.
  //   2. LEGACY fallback — when no quoteItems, fetch tax + charges
  //      via /preview and compute discounts locally (the historical
  //      behaviour, kept so screens not yet migrated still work).
  const [serverQuote, setServerQuote] = useState<BookingQuote | null>(null);
  // Tracked separately from `serverQuote` so we can keep the last good
  // values on screen while a fresh quote is in flight (no flicker to a
  // blank breakdown) but still know the displayed numbers may shift
  // when the response lands. The Total row reads this so it can flag
  // "Updating…" briefly after a coupon or wallet change.
  const [quoteLoading, setQuoteLoading] = useState(false);
  // Track which voucher code the current serverQuote was computed
  // against. The auto-clear "voucher rejected by server" effect
  // below races otherwise: voucherApplied flips to set IMMEDIATELY
  // on Apply, but the quote refetch takes a roundtrip. Without this
  // marker the effect would see the OLD serverQuote (voucher_discount
  // = 0 because it didn't include the new code) and falsely flag the
  // voucher as rejected, even though the next fresh quote would have
  // returned the discount (audit 2026-06-04, voucher race).
  const [serverQuoteForCode, setServerQuoteForCode] = useState<
    string | null
  >(null);
  const itemsKey = quoteItems ? JSON.stringify(quoteItems) : null;
  useEffect(() => {
    if (!quoteItems || quoteItems.length === 0) {
      setServerQuote(null);
      setQuoteLoading(false);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    // Fire on the next microtask — no debounce. Earlier we waited
    // 200 ms before hitting /quote, which is what made coupon-after-
    // wallet feel "wrong": the tax + charges lines still showed the
    // pre-discount values for that window, so the customer sometimes
    // hit Book and Pay while the breakdown was mid-recalc. Quote API
    // is cheap; just re-run it on every meaningful change.
    const requestedCode = voucherApplied?.voucher.code ?? null;
    const t = setTimeout(() => {
      previewBookingQuote({
        items: quoteItems,
        mode: isSubscription ? 'SUBSCRIPTION' : 'ONE_OFF',
        currency: 'GBP',
        // Price off the chosen pro's custom rate so the basket total
        // matches checkout (the +20%-on-top model).
        professional_id: draft?.professional_id ?? null,
        wallet_amount: walletAmount,
        // When the customer's applied a code locally, surface it so
        // the server quote applies the discount and the breakdown
        // line + total reflect what Stripe will actually charge.
        voucher_code: requestedCode,
      })
        .then((res) => {
          if (cancelled) return;
          setServerQuote(res.info);
          setServerQuoteForCode(requestedCode);
          setQuoteLoading(false);
          // Surface the fresh quote (including its pricing_version
          // token) to the parent page so its createOneOff /
          // createSubscription call can echo the version back for
          // the rate-lock check.
          onQuoteChange?.(res.info);
        })
        .catch(() => {
          // Network blip — clear the stale quote so we don't keep
          // showing tax/charges figures that no longer match the
          // current voucher/wallet inputs. Keep `quoteLoading=true`
          // so the customer's "Updating…" hint stays visible until
          // the next attempt succeeds (audit 2026-06-04, medium).
          if (cancelled) return;
          setServerQuote(null);
          onQuoteChange?.(null);
          // Don't flip quoteLoading off; the next user interaction
          // re-fires the effect and will overwrite this state.
        });
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, isSubscription, walletAmount, voucherApplied?.voucher.code, draft?.professional_id]);

  // Refresh the quote when the customer returns to the tab.
  //
  // Without this, a basket sitting in an inactive tab keeps showing
  // a stale total — and if the admin tweaks tax / charges / a
  // service price meanwhile, the basket and the Stripe page would
  // disagree. The rate-lock 409 catches the drift on click, but
  // showing the right number BEFORE the customer hits Pay is
  // friendlier. Cheap call (single POST, no auth state mutation).
  // Bump a counter to retrigger the existing quote effect rather
  // than duplicating the fetch logic.
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setFocusTick((t) => t + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  // Re-run the quote whenever focusTick bumps. Lighter than another
  // copy of the previewBookingQuote call — just nudge the effect by
  // including focusTick in a no-op effect that retriggers via the
  // dependency change above. (We can't add focusTick to the main
  // effect's deps because itemsKey is derived from quoteItems; the
  // safer pattern is a tiny separate effect that calls the same
  // fetcher.) The pattern matches RequireOnboardedPro's "refresh on
  // mount" recipe and keeps the main quote effect single-purposed.
  useEffect(() => {
    if (focusTick === 0) return; // skip initial mount — main effect already fetched
    if (!quoteItems || quoteItems.length === 0) return;
    let cancelled = false;
    const requestedCode = voucherApplied?.voucher.code ?? null;
    previewBookingQuote({
      items: quoteItems,
      mode: isSubscription ? 'SUBSCRIPTION' : 'ONE_OFF',
      currency: 'GBP',
      professional_id: draft?.professional_id ?? null,
      wallet_amount: walletAmount,
      voucher_code: requestedCode,
    })
      .then((res) => {
        if (cancelled) return;
        setServerQuote(res.info);
        setServerQuoteForCode(requestedCode);
        onQuoteChange?.(res.info);
      })
      .catch(() => {
        // Stale quote stays — better than blanking the basket.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTick]);

  // Legacy preview (tax + charges only) — runs only when no quote
  // items are passed so the un-migrated callsites still render
  // somewhat-sane numbers.
  const [pricePreview, setPricePreview] = useState<BookingPricePreview | null>(
    null,
  );
  useEffect(() => {
    if (quoteItems) {
      setPricePreview(null);
      return;
    }
    if (subtotal <= 0) {
      setPricePreview(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      previewBookingPricing({ subtotal })
        .then((res) => {
          if (cancelled) return;
          setPricePreview(res.info);
        })
        .catch(() => {
          /* keep last preview */
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [subtotal, quoteItems]);

  const taxAmount = serverQuote
    ? Number(serverQuote.tax_amount)
    : Number(pricePreview?.tax_amount ?? 0);
  // Platform/service fee added on top of the pro rate (+20% model).
  // VAT-inclusive: taxAmount above is the VAT contained within this fee.
  const platformFee = serverQuote
    ? Number((serverQuote as any).platform_fee_amount ?? 0)
    : 0;
  const chargesBreakdown = serverQuote
    ? serverQuote.charges_breakdown.map((c) => ({
        id: c.id,
        name: c.name,
        type: c.type as any,
        value: c.value,
        amount: c.amount,
        currency: serverQuote.currency,
      }))
    : pricePreview?.charges ?? [];
  const chargesAmount = chargesBreakdown.reduce(
    (acc, c) => acc + Number(c.amount || 0),
    0,
  );
  // Build the breakdown component-by-component so the local voucher
  // (which the /quote endpoint doesn't see yet — we only pass items
  // + wallet) layers correctly on top of the server-computed
  // membership / wallet. Without this, applying a coupon showed the
  // green "£5 saved" caption but the Total didn't move and no
  // "Voucher discount" line appeared in the breakdown.
  const displaySubtotal = serverQuote ? Number(serverQuote.subtotal) : subtotal;
  // Split subtotal into items vs addons so the breakdown renders as
  // two distinct lines per the universal price-breakdown order
  // (items → addons → discounts → tax → charges → wallet → total).
  // Falls back to the local cleaningTotal / addonsTotal preview when
  // the server quote isn't loaded yet (or doesn't yet split them).
  const displayItemsTotal = serverQuote
    ? Number((serverQuote as any).items_total ?? cleaningTotal)
    : cleaningTotal;
  const displayAddonsTotal = serverQuote
    ? Number((serverQuote as any).addons_total ?? addonsTotal)
    : addonsTotal;
  // Per-service line items from the server quote (name + quantity +
  // line total). Drives the multi-service basket display so a booking
  // with several selected services (Hair / Beauty & Wellness /
  // Childcare) lists EVERY one — not just the primary. Add-ons render
  // in their own section below, so they're filtered out here.
  const serviceLineItems = serverQuote
    ? serverQuote.items.filter((it) => !it.service_addon_id)
    : [];
  // The single picked service (when there's exactly one). Used by the
  // single-line display to show the SAME fee-inclusive price the customer
  // saw when selecting (rate + platform fee), instead of the raw `price`
  // prop (the pro rate, fee-exclusive) — which is what made the basket
  // line disagree with the service picker.
  const singleServiceItem =
    serviceLineItems.length === 1 ? serviceLineItems[0] : null;
  const singleLineDisplayPrice =
    pricingUnit === 'PER_HOUR'
      ? (singleServiceItem?.customer_unit_price ?? price)
      : (singleServiceItem?.customer_line_total ?? price);
  const rawSubscriptionDiscount = serverQuote
    ? Number(serverQuote.membership_discount.amount)
    : subscriptionDiscountLocal;
  const serverVoucher = serverQuote
    ? Number(serverQuote.voucher_discount.amount)
    : 0;
  // Once the server quote has loaded, its voucher_discount.amount is
  // the SINGLE source of truth — including the case where it returns
  // zero. A zero from the server means "this code resolves but does
  // not apply to you" (no CustomerVoucher entitlement, expired, below
  // min_booking_amount, etc.), NOT "still loading". Falling back to
  // the public-preview amount in that case displayed a discount the
  // backend would never honor and Stripe over-charged the customer by
  // exactly the voucher amount (audit 2026-06-04, critical #1).
  //
  // We only honour the local preview before the first server quote
  // returns, so the customer sees instant feedback on Apply.
  const rawVoucherDiscount = serverQuote
    ? serverVoucher
    : voucherDiscountLocal;
  // Proportional rescale when the combined discount exceeds the
  // displayable subtotal (items + addons). Without this, a basket
  // with a 15% member discount + a £50 fixed voucher on a £50 cart
  // would render lines that sum to £57.50 of discount on £50 of
  // items — visibly inconsistent (audit 2026-06-04, high). The
  // server quote's `discount_amount` is already capped at subtotal,
  // so we just need to rescale the two visible lines to match.
  const displayableSubtotal =
    Number(displayItemsTotal) + Number(displayAddonsTotal);
  const rawDiscountSum = rawSubscriptionDiscount + rawVoucherDiscount;
  let subscriptionDiscount = rawSubscriptionDiscount;
  let voucherDiscount = rawVoucherDiscount;
  if (rawDiscountSum > displayableSubtotal && rawDiscountSum > 0) {
    const scale = displayableSubtotal / rawDiscountSum;
    voucherDiscount = Math.round(rawVoucherDiscount * scale * 100) / 100;
    subscriptionDiscount =
      Math.round((displayableSubtotal - voucherDiscount) * 100) / 100;
  }
  // Total = sum of every line the breakdown actually renders.
  // Computing it line-by-line guarantees the customer can verify it
  // by hand: items + addons − membership − voucher + tax + charges
  // − wallet. Mixing the server's `grand_total_before_discounts` with
  // a locally-overlaid voucher (the previous approach) drifted by
  // ~£5 during the 200 ms window between "voucher applied" and the
  // next /quote response landing — the displayed Total would lag the
  // discount line by exactly the voucher amount.
  //
  // The server still re-runs the same recipe on every basket change
  // (debounced, deps: items + isSubscription + walletAmount +
  // voucher code) so once the quote settles, every line above is the
  // server's authoritative value and this sum equals what Stripe
  // will charge.
  const round2 = (n: number) => Math.round(n * 100) / 100;
  // Reconstruction matches the displayed breakdown + the +20% model:
  // items + addons − discounts + service fee (VAT-inclusive) + charges.
  // (VAT lives inside `platformFee`, so it is NOT added again here.)
  const grandTotalBeforeWallet = round2(
    Math.max(
      0,
      Number(displayItemsTotal) +
        Number(displayAddonsTotal) -
        Number(subscriptionDiscount) -
        Number(voucherDiscount) +
        Number(platformFee) +
        Number(chargesAmount),
    ),
  );
  // Under the +20%-on-top model (Q8/C6) the bookable total is the
  // server's inclusive `total_amount` (items + addons − discounts +
  // service fee + charges). The itemised breakdown above renders each of
  // those lines and sums to exactly this number. Prefer the server value
  // (authoritative); fall back to the local reconstruction only before
  // the first quote lands (or the legacy no-quote path).
  const afterDiscount = serverQuote
    ? round2(Number(serverQuote.total_amount))
    : grandTotalBeforeWallet;
  // Wallet — capped at the bookable total so we never go negative.
  // Subscriptions skip wallet entirely (Stripe Sub bills the full
  // per-cycle amount). Local clamp keeps the "Wallet − £X" line in
  // sync with the input even before the next /quote round-trip lands.
  const walletApplied = isSubscription
    ? 0
    : Math.min(Math.max(0, Number(walletAmount) || 0), afterDiscount);
  // What hits the card. Everything is local arithmetic on lines the
  // customer can read, so the visible breakdown always sums to the
  // visible Total — no flicker after the next quote settles.
  const totalPrice = round2(Math.max(0, afterDiscount - walletApplied));
  // The membership pill / "subscribe & save" upsell hide whenever
  // the quote returns a membership discount (means the customer has
  // a plan already), even if the local membership API call hasn't
  // landed yet.
  const effectiveMembershipPct = serverQuote
    ? Number(serverQuote.membership_discount.percentage)
    : membershipDiscountPct;

  // ---- All-in (fee-inclusive) breakdown ----
  // The per-service lines show the customer's all-in rate (pro rate +
  // platform fee), so the Item total must agree with them: £18/h × 2 =
  // £36, not the raw £30. The platform fee — and the VAT inside it — are
  // folded INTO the item total here and shown only as an informational
  // "includes VAT" line, never added again. The discount is rescaled to
  // the all-in base and taken as the reconciling REMAINDER against the
  // authoritative server total, so the visible lines always sum exactly
  // to the Total (no 1p drift, even with odd rates).
  const allInItemsTotal = serverQuote
    ? round2(
        serviceLineItems.reduce(
          (s, it) =>
            s + Number((it as any).customer_line_total ?? it.line_total ?? 0),
          0,
        ),
      )
    : displayItemsTotal;
  const allInAddonsTotal = serverQuote
    ? round2(
        serverQuote.items
          .filter((it) => it.service_addon_id)
          .reduce(
            (s, it) =>
              s + Number((it as any).customer_line_total ?? it.line_total ?? 0),
            0,
          ),
      )
    : displayAddonsTotal;
  // allInItems + allInAddons − allInDiscount + VAT + charges === afterDiscount.
  // VAT is now ADDED on top (no longer inside the fee), so it's part of
  // `afterDiscount` (= total_amount). Add it back into the residual so the
  // VAT and charges cancel and what's left is the real discount:
  //   full items + charges + VAT − discounted total = the discount.
  // Only attribute the reconciling remainder to a discount when a real
  // voucher / membership discount is actually applied. Otherwise the
  // remainder can be a PHANTOM: if the server quote still uses the legacy
  // VAT-in-fee model (total_amount already contains the VAT) while this
  // breakdown adds `taxAmount` on top (the June-2026 VAT-on-top model), the
  // remainder lands at exactly the VAT — and we'd render a "Coupon discount"
  // line equal to the VAT with no real saving (the Total never moves, the
  // coupon field is empty). Guarding on the real discount means a VAT/fee
  // model mismatch between a freshly-deployed frontend and a not-yet-deployed
  // backend can never invent a discount. A genuine voucher/membership always
  // has rawVoucher/subscription > 0, so legitimate discounts are unaffected.
  const hasRealDiscount =
    Number(voucherDiscount) + Number(subscriptionDiscount) > 0;
  const allInDiscountTotal = hasRealDiscount
    ? round2(
        Math.max(
          0,
          allInItemsTotal +
            allInAddonsTotal +
            Number(chargesAmount) +
            Number(taxAmount) -
            Number(afterDiscount),
        ),
      )
    : 0;
  // Split the all-in discount back into its membership / voucher lines in
  // the same proportion as the raw (pro-rate) discounts, so each still
  // shows separately and the two sum to the whole.
  const allInRawDiscountSum =
    Number(subscriptionDiscount) + Number(voucherDiscount);
  const allInMembershipDiscount =
    allInRawDiscountSum > 0
      ? round2(
          allInDiscountTotal *
            (Number(subscriptionDiscount) / allInRawDiscountSum),
        )
      : 0;
  const allInVoucherDiscount = round2(
    allInDiscountTotal - allInMembershipDiscount,
  );

  // The "you save" caption must agree with the Coupon discount line in the
  // breakdown. That line shows the all-in (fee-inclusive) figure, but the
  // voucher PREVIEW (voucherApplied.discount_amount) is computed on the raw
  // pro-rate subtotal the basket sends it, so it reads ~20% low (e.g. £14.70
  // vs £17.64 — off by the platform-fee markup). Use the preview value only
  // as an optimistic placeholder until the server quote for THIS code lands,
  // then switch to the same all-in figure the summary renders. (Issue 3)
  const voucherCaptionSaving =
    voucherApplied &&
    serverQuote &&
    (serverQuoteForCode ?? '').trim().toUpperCase() ===
      voucherApplied.voucher.code.trim().toUpperCase()
      ? allInVoucherDiscount
      : Number(voucherApplied?.discount_amount ?? 0);

  const handleApply = async () => {
    if (!canApply) return;
    setCouponLoading(true);
    setCouponError(null);
    try {
      const res = await previewVoucherCode(trimmedCode, subtotal);
      setVoucherApplied(res);
      const meta = {
        ...(getBookingDraft()?.meta ?? {}),
        coupon_code: trimmedCode,
        voucher_id: res.voucher.id,
        voucher_discount_amount: res.discount_amount,
      };
      patchBookingDraft({ meta });
    } catch (err) {
      setCouponError(
        apiErrorMessage(err, "We couldn't apply that coupon."),
      );
      setVoucherApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemove = () => {
    const current = getBookingDraft()?.meta ?? {};
    const {
      coupon_code: _c,
      voucher_id: _v,
      voucher_discount_amount: _d,
      ...rest
    } = current;
    patchBookingDraft({ meta: rest });
    setVoucherApplied(null);
    setCouponCode('');
    setCouponError(null);
  };

  // If the user edits the input after a successful apply, clear the
  // applied voucher so the discount line + total don't keep claiming a
  // discount for a different code than what's currently in the field.
  useEffect(() => {
    if (!voucherApplied) return;
    if (trimmedCode !== voucherApplied.voucher.code.trim()) {
      setVoucherApplied(null);
      const current = getBookingDraft()?.meta ?? {};
      const {
        coupon_code: _c,
        voucher_id: _v,
        voucher_discount_amount: _d,
        ...rest
      } = current;
      patchBookingDraft({ meta: rest });
    }
  }, [trimmedCode, voucherApplied]);

  // If the subtotal materially changes after a voucher was applied
  // (e.g. user changed duration / added an add-on), re-fetch the
  // preview so the displayed discount is honest for the new basket.
  useEffect(() => {
    if (!voucherApplied) return;
    if (subtotal === voucherApplied.discount_amount && subtotal === 0) return;
    let cancelled = false;
    previewVoucherCode(voucherApplied.voucher.code, subtotal)
      .then((res) => {
        if (cancelled) return;
        setVoucherApplied(res);
        const meta = {
          ...(getBookingDraft()?.meta ?? {}),
          coupon_code: res.voucher.code,
          voucher_id: res.voucher.id,
          voucher_discount_amount: res.discount_amount,
        };
        patchBookingDraft({ meta });
      })
      .catch(() => {
        // If the voucher silently became invalid mid-flow (expired,
        // subtotal dropped under min_booking_amount), drop the
        // discount so totals stay honest.
        if (cancelled) return;
        setVoucherApplied(null);
        // Clear from draft so a navigation away + back doesn't
        // resurrect an invalid voucher.
        const current = getBookingDraft()?.meta ?? {};
        const {
          coupon_code: _c,
          voucher_id: _v,
          voucher_discount_amount: _d,
          ...rest
        } = current;
        patchBookingDraft({ meta: rest });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  // Reconcile the optimistic local voucher state with the server's
  // verdict. If the customer applied a code that the public preview
  // accepted, but the authenticated /quote came back with
  // voucher_discount.amount === 0, the entitlement is missing /
  // expired / under-spend — drop the local applied state and surface
  // an inline error so the basket never claims a discount that
  // checkout won't honor (audit 2026-06-04, critical #1).
  //
  // GATING: only fire when the server quote in front of us was
  // computed FOR the currently-applied code. Without the
  // `serverQuoteForCode === voucherApplied.voucher.code` check we'd
  // race: voucherApplied flips to set immediately on Apply, the
  // quote refetch is a roundtrip away, and the stale serverQuote
  // (from before the code was added) has voucher_discount = 0 — we
  // would have falsely rejected a valid voucher (audit 2026-06-04,
  // voucher race).
  useEffect(() => {
    if (!serverQuote) return;
    if (!voucherApplied) return;
    const appliedCode = voucherApplied.voucher.code.trim().toUpperCase();
    const quotedCode = (serverQuoteForCode ?? '').trim().toUpperCase();
    if (appliedCode !== quotedCode) return; // wait for the fresh quote
    const serverAmount = Number(serverQuote.voucher_discount?.amount ?? 0);
    if (serverAmount > 0) return;
    setVoucherApplied(null);
    setCouponError(
      "That code isn't available on your account. Sign in with the account that received it or try a different code.",
    );
    // Strip the rejected code from the draft so it doesn't get
    // resurrected on the next mount via the auto-revalidate effect.
    const current = getBookingDraft()?.meta ?? {};
    const {
      coupon_code: _c,
      voucher_id: _v,
      voucher_discount_amount: _d,
      ...rest
    } = current;
    patchBookingDraft({ meta: rest });
  }, [serverQuote, serverQuoteForCode, voucherApplied]);

  // Auto-revalidate a coupon code that was persisted in the draft (the
  // customer applied it earlier and then navigated away + back). The
  // input shows the saved code on mount but `voucherApplied` starts
  // null, so without this they'd see the code in the field but no
  // green confirmation and the breakdown line wouldn't appear until
  // they clicked Apply again. Gated on subtotal > 0 so we don't fire
  // against an empty basket and 200 ms-delayed so the catalog has
  // time to populate `subtotal` first.
  useEffect(() => {
    if (voucherApplied) return;
    if (couponLoading) return;
    if (subtotal <= 0) return;
    const saved = getBookingDraft()?.meta?.coupon_code;
    if (typeof saved !== 'string' || !saved.trim()) return;
    if (saved.trim() !== trimmedCode) return;
    let cancelled = false;
    setCouponLoading(true);
    previewVoucherCode(saved.trim(), subtotal)
      .then((res) => {
        if (cancelled) return;
        setVoucherApplied(res);
      })
      .catch(() => {
        // Saved code no longer valid — drop it silently so the input
        // doesn't display a code that the server would reject.
        if (cancelled) return;
        setCouponCode('');
        const current = getBookingDraft()?.meta ?? {};
        const {
          coupon_code: _c,
          voucher_id: _v,
          voucher_discount_amount: _d,
          ...rest
        } = current;
        patchBookingDraft({ meta: rest });
      })
      .finally(() => {
        if (!cancelled) setCouponLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  return (
    <div className="w-full lg:w-[361px] lg:flex-shrink-0">
      <div className="border border-[#ede8e9] rounded-[10px] p-[24px] bg-white">
        {/* Logo */}
        <div className="w-[120px] mx-auto mb-6">
          <img src={imgLogowithlcon1} alt="Cherry Picked" className="w-full h-full object-contain" />
        </div>

        {/* My basket Address */}
        <div className="mb-8">
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-base leading-[24px] mb-2">
            My basket
          </h2>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px] leading-[22px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
            {headerAddress}
          </p>
        </div>

        <div className="h-px bg-[#ede8e9] mb-8" />

        {/* Price breakdown */}
        <h3 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[15px] leading-[22px] tracking-[-0.1px] mb-6" style={{ fontVariationSettings: "'opsz' 14" }}>
          Price breakdown
        </h3>

        {/* Service Section (label tracks the selected category, e.g.
            "Cleaning", "Hair", "Beauty & Wellness"). */}
        <div className="mb-6">
          <h4 className="font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-[15px] leading-[22px] mb-4">
            {sectionHeading}
          </h4>
          {serviceLineItems.length > 1 ? (
            // Multi-service booking — list each selected service with its
            // quantity and line total (the server quote is the source of
            // truth, so these sum to the Item total below).
            <div>
              {serviceLineItems.map((it, idx) => (
                <div
                  key={it.service_id ?? `item-${idx}`}
                  className="flex items-start justify-between mb-1"
                >
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px] leading-[22px]">
                    {it.name}
                    {it.quantity > 1 ? (
                      <span className="text-[13px]"> × {it.quantity}</span>
                    ) : null}
                  </p>
                  <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-[22px] text-right whitespace-nowrap">
                    £{Number(it.customer_line_total ?? it.line_total).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-start justify-between mb-1">
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px] leading-[22px]">
                {serviceName}
                {commercialSpaceType && (
                  <>
                    <br />
                    <span className="text-[13px]">
                      {commercialSpaceType === 'other'
                        ? otherCommercialSpace
                        : commercialSpaceLabelById.get(commercialSpaceType) ??
                          commercialSpaceType}
                    </span>
                  </>
                )}
              </p>
              <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-[22px] text-right whitespace-nowrap">
                £{Number(singleLineDisplayPrice).toFixed(2)}
                {pricingUnit === 'PER_HOUR' ? '/h' : ''}
              </p>
            </div>
          )}
          {pricingUnit === 'PER_HOUR' &&
            (isCommercial
              ? // Show the hours whenever we know them: in-flow they
                // arrive via `commercialHours`; at checkout the wizard
                // only passes the `duration` string, so fall back to the
                // parsed `durationHours` (otherwise commercial bookings
                // showed a per-hour rate + total with no hours at all).
                (commercialHours && commercialHours > 0) || durationHours > 0
              : sessionStep >= 3) && (
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px] leading-[22px] mb-1">
                For{' '}
                {isCommercial
                  ? formatHours(
                      commercialHours && commercialHours > 0
                        ? commercialHours
                        : durationHours,
                    )
                  : formatHours(durationHours)}
              </p>
            )}
          {showPets && !isCommercial && sessionStep >= 4 && (
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px] leading-[22px]">
              {hasPets === 'yes' ? 'Pets in property' : 'No pets in property'}
            </p>
          )}
        </div>

        {/* Add-ons Section */}
        {sessionStep >= 10 && selectedAddons.length > 0 && (
          <div className="mb-8">
            <h4 className="font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-[15px] leading-[22px] mb-4">
              Add-ons
            </h4>
            <div className="space-y-4">
              {selectedAddons.map((addon) => {
                const catalogRow = addonsCatalog.find((a) => a.id === addon.id);
                const label =
                  addonNameById.get(addon.id) ??
                  // Last-resort fallback if the catalog hasn't loaded
                  // yet — keeps the row visible instead of showing
                  // `undefined`.
                  'Add-on';
                // Prefer the server quote's all-in line total for this
                // add-on (so the line matches the Addon total exactly).
                // Before the quote lands, fall back to the inclusive
                // (display) catalog price × blocks.
                const serverAddon = serverQuote?.items.find(
                  (it) => it.service_addon_id === addon.id,
                );
                const blockPrice = catalogRow
                  ? Number(catalogRow.display_price ?? catalogRow.price)
                  : price * 0.5;
                const oneHourBlocks = catalogRow
                  ? Math.max(1, Math.round(60 / catalogRow.block_minutes))
                  : 2;
                const totalForAddon = serverAddon
                  ? Number(
                      (serverAddon as any).customer_line_total ??
                        serverAddon.line_total ??
                        0,
                    )
                  : addon.duration === '1hour'
                    ? blockPrice * oneHourBlocks
                    : blockPrice;
                return (
                  <div
                    key={addon.id}
                    className="flex items-start justify-between"
                  >
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px] leading-[22px]">
                      {label} -{' '}
                      {addon.duration === '30min' ? '30 mins' : '1 hour'}
                    </p>
                    <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-[22px]">
                      £{totalForAddon.toFixed(2)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(sessionStep >= 10 && selectedAddons.length > 0) && <div className="h-px bg-[#ede8e9] mb-8" />}

        {/* Appointment Details — just the chosen date/time. The old
            "Other times you've kept open" secondary-slot list was removed:
            customers can't actually select multiple times, so it only ever
            echoed the single chosen slot back as a confusing duplicate. */}
        {sessionStep >= 9 && appointmentDateObj && (
          <div className="mb-8">
            <h4 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[15px] leading-[22px] mb-2">
              {appointmentHeading}
            </h4>
          </div>
        )}

        {sessionStep >= 9 && <div className="h-px bg-[#ede8e9] mb-8" />}

        {/* Pro minimum booking value warning. Checkout hard-rejects below
            the pro's minimum; surface it here so the customer can add more
            before hitting Pay. */}
        {serverQuote &&
        serverQuote.meets_minimum === false &&
        serverQuote.minimum_booking_value ? (
          <div className="mb-8 rounded-[10px] border border-[#f3d6a0] bg-[#fff7e6] p-4">
            <p className="font-['DM_Sans',sans-serif] text-[#9a6700] text-[13px]">
              This professional has a minimum booking of{' '}
              <span className="font-bold">
                £{Number(serverQuote.minimum_booking_value).toFixed(2)}
              </span>
              . Add more services to reach it before booking.
            </p>
          </div>
        ) : null}

        {/* Coupon Code Section — hidden for subscription baskets
            because Stripe Sub takes the full per-cycle amount at
            signup; vouchers don't stack onto recurring charges. */}
        {!isSubscription ? (
          <div className="mb-8 mt-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value);
                  setCouponError(null);
                }}
                placeholder="Coupon code"
                disabled={!!voucherApplied || couponLoading}
                className="flex-1 h-[44px] px-4 rounded-[10px] border border-[#ede8e9] font-['DM_Sans',sans-serif] text-[14px] focus:outline-none focus:border-[#8e2a2f] transition-all placeholder:text-[#908e8e] disabled:bg-[#fcfaf7]"
              />
              {voucherApplied ? (
                <button
                  type="button"
                  onClick={handleRemove}
                  className="px-6 h-[44px] bg-white border border-[#8e2a2f] text-[#8e2a2f] hover:bg-[#fbeaea] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[13px] transition-all uppercase tracking-wide"
                >
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={!canApply}
                  className="px-6 h-[44px] bg-[#8e2a2f] hover:bg-[#7a2428] text-white rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[13px] transition-all uppercase tracking-wide disabled:bg-[#cfcccd] disabled:cursor-not-allowed disabled:hover:bg-[#cfcccd]"
                >
                  {couponLoading ? 'Checking…' : 'Apply'}
                </button>
              )}
            </div>
            {voucherApplied ? (
              <p className="text-[#2e7d32] text-[12px] font-['DM_Sans',sans-serif] mt-2 font-medium">
                {voucherApplied.voucher.title} applied — you save £
                {voucherCaptionSaving.toFixed(2)}.
              </p>
            ) : couponError ? (
              <p className="text-[#8e2a2f] text-[12px] font-['DM_Sans',sans-serif] mt-2 font-medium">
                {couponError}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Use my wallet balance — toggle + amount input. The amount
            field defaults to the smaller of the wallet balance and the
            post-discount total, but the customer can edit it down if
            they want to save some balance for later. Anything they
            don't cover with wallet hits the Stripe card. Hidden for
            subscriptions and for customers with no balance. */}
        {!isSubscription && walletAvailable > 0 && onToggleWallet ? (
          <WalletInput
            walletEnabled={walletEnabled}
            walletAvailable={walletAvailable}
            walletCurrency={walletCurrency}
            walletAmount={walletApplied}
            afterDiscount={afterDiscount}
            onToggle={onToggleWallet}
            onAmountChange={onWalletAmountChange}
          />
        ) : null}

        {/* Universal price-breakdown order (all-in / fee-inclusive):
              1. Item total   (pro rate + platform fee, e.g. £18/h × 2)
              2. Addon total  (same, fee-inclusive)
              3. Membership discount
              4. Coupon / voucher discount
              5. Other charges (card processing, booking fee, …)
              6. Wallet
              7. Final total
            The platform/service fee + its VAT live INSIDE the item total
            and are shown only as an informational "includes VAT" line —
            never added again. Each line hides when its amount is zero. */}

        {/* 1. Item total (sum of the per-service all-in lines above) */}
        {allInItemsTotal > 0 ? (
          <div className="flex items-center justify-between mb-2">
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
              Item total
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
              £{allInItemsTotal.toFixed(2)}
            </p>
          </div>
        ) : null}

        {/* 2. Addon total */}
        {allInAddonsTotal > 0 ? (
          <div className="flex items-center justify-between mb-2">
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
              Addon total
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
              £{allInAddonsTotal.toFixed(2)}
            </p>
          </div>
        ) : null}

        {/* 3. Membership discount — rescaled to the all-in base. */}
        {allInMembershipDiscount > 0 ? (
          <div className="flex items-center justify-between mb-2">
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
              Membership discount ({effectiveMembershipPct}%)
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#2e7d32] text-[14px] font-bold">
              − £{allInMembershipDiscount.toFixed(2)}
            </p>
          </div>
        ) : null}

        {/* 4. Coupon / voucher discount — rescaled to the all-in base. */}
        {allInVoucherDiscount > 0 ? (
          <div className="flex items-center justify-between mb-2">
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
              Coupon discount
              {voucherApplied?.voucher?.code
                ? ` (${voucherApplied.voucher.code})`
                : ''}
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#2e7d32] text-[14px] font-bold">
              − £{allInVoucherDiscount.toFixed(2)}
            </p>
          </div>
        ) : null}

        {/* 5. VAT — charged ON our platform commission and ADDED to the total
            (it is not a flat % of the whole order), so this line contributes to
            the running total below. The label reads "(on platform commission)"
            rather than a flat % so customers don't read it as VAT on the total. */}
        {taxAmount > 0 ? (
          <div className="flex items-center justify-between mb-2">
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
              {serverQuote?.tax_name ?? 'VAT'} (on platform commission)
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
              £{taxAmount.toFixed(2)}
            </p>
          </div>
        ) : null}

        {/* 6. Other charges — admin-configured (booking fee, card
              processing, etc.). */}
        {chargesBreakdown.map((c) => (
          <div key={c.id} className="flex items-center justify-between mb-2">
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
              {c.name}
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
              £{Number(c.amount).toFixed(2)}
            </p>
          </div>
        ))}

        {/* 7. Wallet — applied if the customer ticked the toggle. */}
        {walletApplied > 0 ? (
          <div className="flex items-center justify-between mb-3">
            <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
              Wallet
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#2e7d32] text-[14px] font-bold">
              − £{walletApplied.toFixed(2)}
            </p>
          </div>
        ) : null}

        {/* 8. Final total — what hits the customer's card after the
              wallet portion is debited separately. The "Updating…"
              flag appears while the quote API is in flight so the
              customer knows the figure can shift slightly when the
              fresh server values for tax / charges arrive (matters
              when a coupon is applied AFTER wallet, since the tax
              base drops). */}
        <div className="flex items-center justify-between">
          <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-lg leading-[24px]">
            Total
          </h3>
          <div className="text-right">
            <p className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-xl leading-[27px]">
              £{totalPrice.toFixed(2)}
            </p>
            {quoteLoading ? (
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px] mt-1">
                Updating…
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* "Want to save on this booking?" upsell — hidden when:
            - the customer already has an active membership (no point
              selling them the discount they already get), or
            - the basket contains an EOT/Commercial line, which the
              backend flags via `membership_eligible: false` (those
              flows are out of scope for the £15/mo plan entirely). */}
      {allowMembershipUpsell &&
      !hasActiveMembership &&
      !(serverQuote && serverQuote.membership_discount.amount > 0) &&
      !(serverQuote && serverQuote.membership_eligible === false) ? (
        <div className="mt-4 bg-[#f2eee2]/50 rounded-[10px] p-[24px]">
          <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-base leading-[24px] mb-4">
            Want to save on this booking?
          </h3>
          <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-sm leading-[21px] tracking-[-0.1px] mb-6" style={{ fontVariationSettings: "'opsz' 14" }}>
            Subscribe now and unlock discounts on every booking plus loyalty
            perks.
          </p>
          <button
            onClick={onSubscribeClick}
            className="w-full h-11 bg-[#8e2a2f] hover:bg-[#7a2428] text-white text-sm font-['DM_Sans',sans-serif] leading-[18px] tracking-[-0.1px] rounded-[10px] transition-colors cursor-pointer" style={{ fontVariationSettings: "'opsz' 14" }}>
            Subscribe & Save
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Wallet pick-amount control. Splits checkbox + amount input from the
 * main basket render so the local input state (what the user is
 * currently typing — including transient invalid values like "1.") can
 * be tracked without leaking back into the canonical numeric prop.
 *
 * The numeric value lives on the parent (BookingBasket → page state);
 * the text value here only exists to make the input feel natural while
 * typing. We sync it with the prop whenever the prop changes (e.g. the
 * basket clamped the amount after a discount lowered the total below
 * the previous wallet pick).
 */
function WalletInput({
  walletEnabled,
  walletAvailable,
  walletCurrency,
  walletAmount,
  afterDiscount,
  onToggle,
  onAmountChange,
}: {
  walletEnabled: boolean;
  walletAvailable: number;
  walletCurrency: string;
  walletAmount: number;
  afterDiscount: number;
  onToggle: (enabled: boolean) => void;
  onAmountChange?: (n: number) => void;
}) {
  // Cap = whichever runs out first. There's no point letting the user
  // type more than the balance OR more than what's actually owed.
  const cap = Math.max(
    0,
    Math.min(Number(walletAvailable) || 0, Number(afterDiscount) || 0),
  );

  const [draft, setDraft] = useState<string>(() =>
    walletAmount > 0 ? walletAmount.toFixed(2) : '',
  );

  // Re-sync the input whenever the canonical amount changes from
  // outside (toggle on/off, post-clamp from a smaller cap, voucher
  // applied, etc.). Skipping this would leave a stale string in the
  // field after the basket clamped it server-side.
  useEffect(() => {
    setDraft(walletAmount > 0 ? walletAmount.toFixed(2) : '');
  }, [walletAmount]);

  // If the cap shrinks below the customer's previously-chosen wallet
  // amount (typical when a coupon is applied AFTER the wallet — the
  // bill drops below the wallet pick), push the parent's state down to
  // the new cap. Without this the parent kept the bigger number while
  // the display silently showed the clamped value, which is what made
  // the "calculation looks wrong" reports surface for coupon-after-
  // wallet flows. Only triggers on a genuine decrease so we don't
  // overwrite what the customer is currently typing.
  useEffect(() => {
    if (!walletEnabled) return;
    if (walletAmount > cap) {
      onAmountChange?.(cap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cap, walletEnabled]);

  const clamp = (n: number) => Math.max(0, Math.min(cap, n));

  const handleToggle = (next: boolean) => {
    onToggle(next);
    if (next) {
      // Default to using the maximum allowed amount — matches what
      // the basket was already doing implicitly. The customer can
      // immediately dial it down.
      const def = cap;
      onAmountChange?.(def);
    } else {
      onAmountChange?.(0);
    }
  };

  const commit = (raw: string) => {
    const parsed = Number(raw.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(parsed)) {
      onAmountChange?.(0);
      return;
    }
    const next = clamp(Math.round(parsed * 100) / 100);
    onAmountChange?.(next);
  };

  return (
    <div className="mb-6 rounded-[10px] border border-[#ede8e9] bg-[#fcfaf7] p-3">
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={walletEnabled}
          onChange={(e) => handleToggle(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#8e2a2f] cursor-pointer"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[13px]">
              Use my wallet balance
            </p>
            <p className="font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-[13px] whitespace-nowrap">
              {walletCurrency} {Number(walletAvailable).toFixed(2)}
            </p>
          </div>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px] leading-[16px] mt-0.5">
            {walletEnabled
              ? `Enter how much to use (max ${walletCurrency} ${cap.toFixed(2)}). The rest goes on your card.`
              : "We'll apply what you choose — the rest goes on your card."}
          </p>
        </div>
      </label>

      {walletEnabled ? (
        <div className="mt-3 flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-bold pointer-events-none">
              {walletCurrency === 'GBP' ? '£' : walletCurrency}
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={draft}
              onChange={(e) => {
                // Allow free typing of partial numbers (".5", "12.",
                // empty). Commit on blur — typing "1" shouldn't
                // immediately clamp to 1.00.
                const v = e.target.value;
                if (/^[0-9]*\.?[0-9]{0,2}$/.test(v) || v === '') {
                  setDraft(v);
                }
              }}
              onBlur={() => {
                if (draft === '' || draft === '.') {
                  setDraft('0.00');
                  onAmountChange?.(0);
                  return;
                }
                commit(draft);
              }}
              className="w-full h-[40px] pl-7 pr-3 rounded-[8px] border border-[#ede8e9] bg-white font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] outline-none focus:border-[#8e2a2f]"
              placeholder="0.00"
              aria-label="Wallet amount to apply"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setDraft(cap.toFixed(2));
              onAmountChange?.(cap);
            }}
            disabled={cap <= 0}
            className="h-[40px] px-3 rounded-[8px] border border-[#8e2a2f] bg-white text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[12px] hover:bg-[#fdf6f6] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Use max
          </button>
        </div>
      ) : null}
    </div>
  );
}
